/**
 * Coordinates draft-completion notifications between the two independent
 * observers that can each detect a draft finishing:
 *   1. The Contract Drafting detail page's status-transition effect
 *      (fires only while the user is on that request's detail page and it
 *      observes a live `*_IN_PROGRESS` -> `DRAFT_GENERATED`/`NEEDS_ATTENTION`
 *      transition).
 *   2. The app-wide `useGlobalJobPoller` hook (fires from anywhere for any
 *      registered active job).
 *
 * Both may observe the same completion (e.g. while the user is on the detail
 * page). This registry guarantees that exactly one of them shows the toast:
 * whichever calls `claimDraftNotification` first "wins" and shows the toast;
 * the other sees the existing claim and stays silent. Crucially this never
 * drops a notification the way route-based suppression could: if the detail
 * page misses the transition (e.g. the user navigated away while the draft
 * completed and returned to an already-finished draft), the poller is still
 * free to claim and notify.
 *
 * `releaseDraftNotification` clears a prior claim so a subsequent (re)generation
 * or retry of the same request notifies again.
 */
const claimedRequestIds = new Set<string>();

/**
 * Atomically claims the completion notification for a request.
 *
 * @returns `true` if the caller should show the toast (it was the first to
 *   claim this request), `false` if another observer already claimed it.
 */
export const claimDraftNotification = (requestId: string): boolean => {
  if (claimedRequestIds.has(requestId)) return false;
  claimedRequestIds.add(requestId);
  return true;
};

/**
 * Releases a prior claim so the next completion for this request notifies.
 * Call this whenever a new draft run begins (generate or retry).
 */
export const releaseDraftNotification = (requestId: string): void => {
  claimedRequestIds.delete(requestId);
};

/** Test-only helper: clears all claims. */
export const __resetDraftNotifyRegistry = (): void => {
  claimedRequestIds.clear();
};
